import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import Img from "@insite/mobius/Img";
import TooltipHover, { TooltipHoverPresentationProps } from "@waytek/MobiusOverrides/Tooltip/TooltipHover";
import { tooltipHover } from "@waytek/Utilities/styleguide";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    properties: { [key: string]: string };
    gap?: number;
    size?: number;
    forceAlignment?: boolean;
}

type propertyType =
    | "additionalshippingfeesapply"
    | "madeinusa"
    | "productoftheyear"
    | "reachcompliant"
    | "rohscompliant"
    | "isnew";

const mapStateToProps = (state: ApplicationState) => {
    const customSettings = getSettingsCollection(state).customSettings as { [key: string]: string };
    const {
        additionalshippingfeesapplyImagePath: additionalshippingfeesapply,
        madeinUSAImagePath: madeinusa,
        productoftheYearImagePath: productoftheyear,
        reachCompliantImagePath: reachcompliant,
        roHSCompliantImagePath: rohscompliant,
        isNewProductPath: isnew,
    } = customSettings;
    return {
        icons: {
            additionalshippingfeesapply,
            madeinusa,
            productoftheyear,
            reachcompliant,
            rohscompliant,
            isnew,
        },
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductLabelsStyles {
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    tooltip?: TooltipHoverPresentationProps;
}

export const productLabelsStyles: ProductLabelsStyles = {
    gridContainer: {
        css: css`
            margin: 0;
        `,
    },

    gridItem: {
        css: css`
            width: auto;
            max-width: none;
            flex-basis: auto;
        `,
    },

    tooltip: {
        cssOverrides: {
            tooltipContainer: css`
                ${tooltipHover.defaultProps?.cssOverrides?.tooltipContainer};
                margin-bottom: 7px;
            `,

            tooltipNotClickable: css`
                padding: 0;
                line-height: 0;

                ${IconWrapper} {
                    top: 0;

                    img {
                        height: 100%;
                        width: 100%;
                    }
                }
            `,
        },
    },
};

const styles = productLabelsStyles;
const propertyPrefix = "cpl_";

const ProductLabels: React.FC<Props> = ({ properties, gap, size, forceAlignment, icons }) => {
    const items: Array<any> = [];

    Object.entries(properties).forEach(([key, value]) => {
        if (!key.startsWith(propertyPrefix) || value.toLowerCase() !== "true") {
            return;
        }

        const property = key.replace(propertyPrefix, "").toLowerCase() as propertyType;
        const labelIcon = () => {
            return <Img width={size} height={size} src={`${icons[property]}`}></Img>;
        };

        items.push(
            <GridItem key={key} {...styles.gridItem}>
                <TooltipHover
                    {...styles.tooltip}
                    iconProps={{ size, src: labelIcon }}
                    // eslint-disable-next-line spire/avoid-dynamic-translate
                    text={translate(key)}
                    forceAlignment={forceAlignment}
                />
            </GridItem>,
        );
    });

    if (properties.isNew && properties.isNew.toLowerCase() === "true") {
        const labelIcon = () => {
            return <Img width={size} height={size} src={`${icons.isnew}`}></Img>;
        };

        items.push(
            <GridItem key="cpl_Newproduct" {...styles.gridItem}>
                <TooltipHover
                    {...styles.tooltip}
                    iconProps={{ size, src: labelIcon }}
                    text={translate("cpl_Newproduct")}
                    forceAlignment={forceAlignment}
                />
            </GridItem>,
        );
    }

    return items.length >= 1 ? (
        <GridContainer gap={gap} {...styles.gridContainer} data-test-selector="productLabelsContainer">
            {items.map(item => item)}
        </GridContainer>
    ) : null;
};

ProductLabels.defaultProps = {
    gap: 10,
    size: 24,
    forceAlignment: false,
};

export default connect(mapStateToProps)(React.memo(ProductLabels));
